// Additional Service Requirements for VW Feature Apps
import {
  createUseBrandFeatureService,
  EmobilityLayerButtonChannelService,
  EmobilityNaturalLanguageChannelService,
  EmobilityTariffsChannelService,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {Logger} from '@feature-hub/core';
import {DataVersionServiceV1} from '@volkswagen-onehub/data-version-service';
import {TrackingManagerV1} from '@volkswagen-onehub/tracking-service';
import {LayerManagerV27} from '@volkswagen-onehub/layer-manager';
import {FaContentServiceV1} from '@volkswagen-onehub/fa-content-service';
import {UserInteractionServiceV2} from '@volkswagen-onehub/user-interaction-service';
import {SingleConfigurationFlowNavigatorV1} from '@volkswagen-onehub/single-configuration-flow-navigator';
import {NavigationServiceV1} from '@volkswagen-onehub/navigation-service';
import {ServiceConfigEndpointProvider} from '@volkswagen-onehub/service-config-endpoint-provider';

export enum VwFeatureServiceIds {
  s2Logger = 's2:logger',
  dataVersionService = 'data-version-service',
  trackingManager = 'tracking',
  layerManager = 'layer-manager',
  tariffsChanelService = 'emobility-tariffs-channel-service',
  layerButtonChannelService = 'emobility-layer-button-channel-service',
  naturalLanguageChannelService = 'emobility-natural-language-channel-service',
  contentService = 'fa-content-service',
  userInteractionService = 'user-interaction-service',
  singleConfigurationFlowNavigator = 'single-configuration-flow-navigator',
  s2History = 's2:history',
  serviceConfigEndpointProvider = 'service-config-endpoint-provider',
}

export type RequiredVwFeatureServices = {
  [VwFeatureServiceIds.dataVersionService]: DataVersionServiceV1;
  [VwFeatureServiceIds.trackingManager]: TrackingManagerV1;
  [VwFeatureServiceIds.layerManager]: LayerManagerV27;
  [VwFeatureServiceIds.contentService]: FaContentServiceV1;
  [VwFeatureServiceIds.userInteractionService]: UserInteractionServiceV2;
  [VwFeatureServiceIds.singleConfigurationFlowNavigator]: SingleConfigurationFlowNavigatorV1;
  [VwFeatureServiceIds.s2History]: NavigationServiceV1;
  [VwFeatureServiceIds.serviceConfigEndpointProvider]: ServiceConfigEndpointProvider;
};

export type OptionalVwFeatureServices = {
  [VwFeatureServiceIds.s2Logger]: Logger;
};

export type CustomOptionalVwFeatureServices = {
  [VwFeatureServiceIds.tariffsChanelService]: EmobilityTariffsChannelService;
  [VwFeatureServiceIds.layerButtonChannelService]: EmobilityLayerButtonChannelService;
  [VwFeatureServiceIds.naturalLanguageChannelService]: EmobilityNaturalLanguageChannelService;
};

export const requiredVwServices: Record<
  keyof RequiredVwFeatureServices,
  string
> = {
  [VwFeatureServiceIds.dataVersionService]: '^1.0.0',
  [VwFeatureServiceIds.trackingManager]: '^1.0.0',
  [VwFeatureServiceIds.layerManager]: '^2.7.1',
  [VwFeatureServiceIds.contentService]: '^1.0.0',
  [VwFeatureServiceIds.userInteractionService]: '^1.0.0',
  [VwFeatureServiceIds.singleConfigurationFlowNavigator]: '^1.2.0',
  [VwFeatureServiceIds.s2History]: '^2.0.0',
  [VwFeatureServiceIds.serviceConfigEndpointProvider]: '^1.0.0',
};

export const optionalVwServices: Record<
  keyof OptionalVwFeatureServices,
  string
> = {
  [VwFeatureServiceIds.s2Logger]: '^1.0.0',
};

export const customOptionalVwServices: Record<
  keyof CustomOptionalVwFeatureServices,
  string
> = {
  [VwFeatureServiceIds.tariffsChanelService]: '^1.0.0',
  [VwFeatureServiceIds.layerButtonChannelService]: '^1.0.0',
  [VwFeatureServiceIds.naturalLanguageChannelService]: '^1.0.0',
};

export const useVwFeatureService = createUseBrandFeatureService<
  RequiredVwFeatureServices & Partial<OptionalVwFeatureServices>
>();
